import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import api from './api';

const ReviewsContext = createContext(null);

const STAR_KEYS = ['1', '2', '3', '4', '5'];

// Merge the on-site rating aggregate with the live Trustpilot aggregate into a
// single combined score + star distribution. Trustpilot only exposes a per-star
// breakdown (not individual reviews), which is enough for an accurate blended
// average. When Trustpilot is unavailable or has no reviews, this returns the
// internal aggregate unchanged.
function mergeSummary(internal = {}, tp = null) {
  const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const idist = internal.distribution || {};
  STAR_KEYS.forEach((k) => { dist[k] += Number(idist[k] || 0); });
  if (tp && tp.distribution) {
    STAR_KEYS.forEach((k) => { dist[k] += Number(tp.distribution[k] || 0); });
  }
  let count = 0;
  let sum = 0;
  STAR_KEYS.forEach((k) => { const c = dist[k]; count += c; sum += Number(k) * c; });
  const average = count ? sum / count : 0;
  return {
    ...internal,
    average,
    count,
    distribution: dist,
    trustpilot_count: tp ? Number(tp.total_reviews || 0) : 0,
  };
}

export function ReviewsProvider({ children }) {
  const [summary, setSummary] = useState({ average: 0, count: 0, distribution: {}, my_review: null, trustpilot_count: 0 });
  const [trustpilot, setTrustpilot] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [s, r, tp] = await Promise.all([
        api.getReviewSummary(),
        api.getReviews(12),
        api.getTrustpilotSummary('de-DE').catch(() => ({ data: null })),
      ]);
      const internal = s.data || {};
      const tpData = tp && tp.data && tp.data.available ? tp.data : null;
      setTrustpilot(tpData);
      setSummary(mergeSummary(internal, tpData));
      setReviews((r.data && r.data.reviews) || []);
    } catch (e) {
      console.error('reviews: failed to load summary/list:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = useCallback(async ({ rating, comment, name }) => {
    await api.submitReview({ rating, comment, name });
    await refresh();
  }, [refresh]);

  // Native share (mobile) / clipboard-copy (desktop) helper for recommending
  // the app. `t` is the translator; `context` records WHERE the share started.
  const shareApp = useCallback(async (t, context = 'app') => {
    const tr = (k, fb) => { try { const v = t && t(k); return v && v !== k ? v : fb; } catch (e) { return fb; } };
    const url = `${window.location.origin}/`;
    const data = {
      title: 'Barcode Studio',
      text: tr('review_share_text', 'Erstelle kostenlos Barcodes & QR-Codes mit Barcode Studio'),
      url,
    };
    let method = 'unknown';
    try {
      if (navigator.share) {
        await navigator.share(data);
        method = 'native';
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        method = 'copy';
        toast.success(tr('review_share_copied', 'Link in die Zwischenablage kopiert!'));
      } else {
        window.open(url, '_blank', 'noopener');
        method = 'window';
      }
      // Record only successful shares (native share rejects on cancel).
      try { api.recordShare({ method, context }); } catch (e) { console.error('reviews: non-critical error:', e); }
    } catch (e) { /* user cancelled the share dialog */ }
  }, []);

  // Subtle nudge after a successful download/export. Shows at most once per
  // browser session per type. First we invite a rating (only if not rated yet);
  // once that has been shown/handled, we instead show a discreet "share &
  // recommend" prompt. `t` is the translator passed by the caller so this stays
  // decoupled from i18n.
  const nudgeAfterDownload = useCallback((t) => {
    try {
      const tr = (k, fb) => { try { const v = t && t(k); return v && v !== k ? v : fb; } catch (e) { return fb; } };
      const alreadyRated = !!(summary && summary.my_review);
      const rateShown = window.sessionStorage.getItem('bc_review_nudge') === '1';

      // 1) Rating nudge — only if the user hasn't rated and hasn't seen it yet.
      if (!alreadyRated && !rateShown) {
        window.sessionStorage.setItem('bc_review_nudge', '1');
        window.setTimeout(() => {
          toast(tr('rate_nudge_title', 'Gefällt dir Barcode Studio?'), {
            description: tr('rate_nudge_desc', 'Bewerte uns in wenigen Sekunden – das hilft anderen sehr.'),
            duration: 9000,
            action: {
              label: tr('rate_nudge_cta', 'Bewerten'),
              onClick: () => {
                const el = document.getElementById('reviews');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  window.setTimeout(() => {
                    try {
                      const star = el.querySelector('[data-testid="review-form-card"] button, [data-testid="review-form-card"] [role="radio"]');
                      if (star) star.focus();
                    } catch (e) { console.error('reviews: non-critical error:', e); }
                  }, 650);
                }
              },
            },
          });
        }, 1500);
        return;
      }

      // 2) Otherwise a discreet "share & recommend" nudge (once per session).
      if (window.sessionStorage.getItem('bc_share_nudge') === '1') return;
      window.sessionStorage.setItem('bc_share_nudge', '1');
      window.setTimeout(() => {
        toast(tr('share_nudge_title', 'Barcode Studio weiterempfehlen'), {
          description: tr('share_nudge_desc', 'Gefällt dir das Tool? Teile es mit einem Klick.'),
          duration: 8000,
          action: {
            label: tr('share_nudge_cta', 'Teilen'),
            onClick: () => shareApp(t, 'download_nudge'),
          },
        });
      }, 1500);
    } catch (e) { console.error('reviews: non-critical error:', e); }
  }, [summary, shareApp]);

  return (
    <ReviewsContext.Provider value={{ summary, trustpilot, reviews, loading, refresh, submit, nudgeAfterDownload, shareApp }}>
      {children}
    </ReviewsContext.Provider>
  );
}

export function useReviews() {
  const ctx = useContext(ReviewsContext);
  if (!ctx) throw new Error('useReviews must be used within ReviewsProvider');
  return ctx;
}
